import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatTable } from '@angular/material/table';
import { ISignalRNotification } from '@interfaces/signalr-notification';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { MoveUserService } from '../../modules/move-user/services/move-user.service';
import { LogService } from '@services/log.service';

// Note: There is a linter error indicating this component is declared by more than one NgModule.
// This is a configuration issue that should be fixed by ensuring the component is only declared
// in one NgModule and imported from that module elsewhere. This cannot be fixed in this file.
@Component({
  selector: 'app-move-to-status',
  templateUrl: './move-to-status.component.html',
  styleUrls: ['./move-to-status.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoveToStatusComponent implements OnInit, OnDestroy, AfterViewInit {
  /* table  */
  @ViewChild('statusTable') table: MatTable<Element>;
  @Input() displayedColumns: string[] = [];
  @Input() dataSource: any;
  @Input() showToListButton: boolean = false;
  @Input() isAssign: boolean = false;
  @Input() moveMessages$: Observable<ISignalRNotification>;
  @Output() onAfterViewInit = new EventEmitter<void>();

  private moveMessagesSubscription: Subscription;
  public processingUsers: Set<number> = new Set<number>();
  //private nextItemInQueue: any = null;
  private idPropertyName: string = 'userId'; // Default to userId for backward compatibility

  public isProcessing: boolean = true;

  private _cssClasses: string = '';
  public get cssClasses(): string {
    return this._cssClasses;
  }

  public set cssClass(value: string) {
    try {
      this.renderer.removeClass(this._elementRef.nativeElement, this._cssClasses);
      this._cssClasses = value;
      if (value && value !== '') {
        this.renderer.addClass(this._elementRef.nativeElement, value);
      }
    } catch (error) {
      this.logger.error('Error setting cssClass:', error);        
    }
  }

  @Input() buttonText: string = null;
  @Input() title = '';
  @Input() subtitle = '';

  /**
   * Optional resource key rendered under the subtitle. Additive - callers that do not set it are
   * unaffected. Move Locations uses it to say the affiliation assignment was updated, and only when
   * one actually was.
   */
  @Input() notice: string | null = null;
  @Input() exportToExcelFileName = 'Bulk_Update_Summary_Export';

  @Output() close = new EventEmitter<void>();
  @Output() toListButtonClick = new EventEmitter<void>();

  constructor(
    private renderer: Renderer2,
    private _elementRef: ElementRef,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private _cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private logger: LogService
  ) {
    if (!data) return;

    this.dataSource = data.results;
    this.showToListButton = data.showToListButton;
    this.isAssign = data.isAssign;
    this.buttonText = data.buttonText;
    this.title = data.title;
    this.subtitle = data.subtitle;
    this.exportToExcelFileName = data.exportToExcelFileName;
    this.displayedColumns = data.displayedColumns;
    this.moveMessages$ = data.moveMessages$;

    // Set initial status to 'Waiting' for items without a status
    if (this.dataSource) {
      this.dataSource.forEach((item: any) => {
        if (!item.status || item.status === 'Unknown') {
          item.status = 'Waiting';
        }
      });
    }
  }

  ngOnInit() {
    // Initialize from data if provided
    if (this.data) {
      if (this.data.results) {
        this.dataSource = this.data.results;
      }
      if (this.data.displayedColumns) {
        this.displayedColumns = this.data.displayedColumns;
      }
      if (this.data.showToListButton !== undefined) {
        this.showToListButton = this.data.showToListButton;
      }
      if (this.data.isAssign !== undefined) {
        this.isAssign = this.data.isAssign;
      }
      if (this.data.title) {
        this.title = this.data.title;
      }
      if (this.data.subtitle) {
        this.subtitle = this.data.subtitle;
      }
      if (this.data.exportToExcelFileName) {
        this.exportToExcelFileName = this.data.exportToExcelFileName;
      }
      if (this.data.moveMessages$) {
        this.moveMessages$ = this.data.moveMessages$;

        // Subscribe to move messages
        this.logger.log('Subscribing to move messages');
        this.moveMessagesSubscription = this.moveMessages$.subscribe(notification => {
          this.handleMoveNotification(notification);
        });
      }
    }

    // Initialize processing state
    this.initializeProcessingState();

    // Log initial state
    this.logger.log('MoveToStatus initialized with:', {
      dataSource: this.dataSource ? this.dataSource.length : 0,
      displayedColumns: this.displayedColumns,
      isProcessing: this.isProcessing,
      hasRealTimeUpdates: this.hasRealTimeUpdates(),
    });
  }

  ngAfterViewInit(): void {
    this.onAfterViewInit.emit();
  }

  /**
   * Initialize the processing state based on the current data
   */
  private initializeProcessingState() {
    // Only initialize if we have data
    if (!this.dataSource || !this.dataSource.length) return;

    // Determine the ID property name based on the first item
    const possibleIdProps = ['userId', 'tankId', 'locationId', 'id'];
    for (const prop of possibleIdProps) {
      if (this.dataSource[0][prop] !== undefined) {
        this.idPropertyName = prop;
        break;
      }
    }

    // Initialize processing users set
    this.processingUsers.clear();

    // Ensure all items have a status
    this.dataSource.forEach((item: any) => {
      // If the item doesn't have a status, has an empty status, or has a non-final status that isn't 'Waiting',
      // set it to "Waiting"
      if (
        !item.status ||
        item.status.trim() === '' ||
        (!this.isStatusFinal(item.status) && item.status !== 'Waiting')
      ) {
        this.logger.log(`Setting item ${this.getItemId(item)} status to Waiting (was: ${item.status})`);
        item.status = 'Waiting';
      }

      // If the item is in progress, add to processing set
      if (item.status === 'In Progress') {
        const itemId = this.getItemId(item);
        if (itemId !== undefined) {
          this.processingUsers.add(itemId);
        }
      }
    });

    // Check if all items are complete and update isProcessing flag
    const allComplete = this.areAllItemsComplete();
    this.isProcessing = !allComplete;

    // Find the next item in queue
    // this.updateNextItemInQueue();

    this.logger.log('Processing state initialized:', {
      isProcessing: this.isProcessing,
      idPropertyName: this.idPropertyName,
      processingUsers: this.processingUsers.size,
    });
  }

  ngOnDestroy() {
    if (this.moveMessagesSubscription) {
      this.moveMessagesSubscription.unsubscribe();
    }
  }

  handleMoveNotification(notification: ISignalRNotification) {
    if (!notification) return;

    this.logger.log('MoveToStatus received notification:', notification);

    // Process item-specific notifications
    if (notification.data) {
      const itemData = notification.data;
      const itemId = this.getItemId(itemData);

      if (itemId !== undefined) {
        this.logger.log(`Processing notification for item ${itemId}:`, notification);

        // Find the item in the data source
        const item = this.dataSource.find(i => this.getItemId(i) === itemId);

        if (item) {
          const previousStatus = item.status;

          // If we receive any notification for a waiting item, mark it as in progress
          if (previousStatus === 'Waiting') {
            this.logger.log(`Updating item ${itemId} from Waiting to In Progress`);
            item.status = 'In Progress';
            // Add to processing set to show spinner
            this.processingUsers.add(itemId);
          }

          // If the notification contains a status, update the item
          if (itemData.status) {
            this.logger.log(`Updating item ${itemId} status from ${item.status} to ${itemData.status}`);
            item.status = itemData.status;

            // If the status is final, remove from processing set
            if (this.isStatusFinal(itemData.status)) {
              this.logger.log(`Item ${itemId} has reached final status: ${itemData.status}`);
              this.processingUsers.delete(itemId);
            }
          }

          // Update the UI
          //this.updateNextItemInQueue();
          this._cdr.markForCheck();
          this._cdr.detectChanges();

          // Check if all items are complete
          this.updateCompletionStatus();
        } else {
          this.logger.log(`Item ${itemId} not found in data source`);
        }
      }
    }

    // Log the current state of all items for debugging
    if (this.dataSource && this.dataSource.length) {
      this.logger.log('Current status of all items:');
      this.dataSource.forEach(item => {
        this.logger.log(
          `Item ${this.getItemId(item)}: status=${
            item.status
          }, isProcessing=${this.processingUsers.has(this.getItemId(item))}`
        );
      });
    }
  }

  /** For a caller that only knows what to say once the items have finished. */
  setSubtitle(subtitle: string): void {
    this.subtitle = subtitle;
    this._cdr.markForCheck();
  }

  /**
   * Ends every item still open as failed, for when no result will ever arrive for them. Without this
   * the close buttons stay disabled for good.
   */
  failOpenItems(): void {
    (this.dataSource ?? []).forEach((item: any) => {
      if (!this.isStatusFinal(item.status)) {
        item.status = 'Failed';
        this.processingUsers.delete(this.getItemId(item));
      }
    });
    this.areAllItemsComplete();
    this._cdr.detectChanges();
  }

  /**
   * Determines whether to show a spinner for an item based on its status
   */
  shouldShowSpinner(item: any, status: string): boolean {
    // Get the item ID
    const itemId = this.getItemId(item);
    if (!itemId) {
      return false;
    }

    const isInProgress = status === 'In Progress';
    return isInProgress;
  }

  /**
   * Determines if a status is final (completed or failed)
   */
  private isStatusFinal(status: string): boolean {
    if (!status) return false;

    const lowerStatus = status.toLowerCase();

    return (
      lowerStatus === 'success' ||
      lowerStatus === 'complete' ||
      lowerStatus === 'completed' ||
      lowerStatus === 'failed' ||
      lowerStatus === 'error' ||
      lowerStatus === 'rejected' ||
      lowerStatus === 'skipped' ||
      lowerStatus === 'done'
    );
  }

  /**
   * Check if all items have completed processing (success or error)
   * Only relevant when using real-time updates
   */
  areAllItemsComplete(): boolean {
    if (!this.hasRealTimeUpdates() || !this.dataSource) return true;

    // Check if all items have a final status
    const allComplete = this.dataSource.every((item: any) => this.isStatusFinal(item.status));

    // Update the isProcessing flag based on completion status
    if (this.isProcessing && allComplete) {
      this.isProcessing = false;
      // Trigger change detection to update the UI (enable buttons)
      this._cdr.markForCheck();
    }

    return allComplete;
  }

  /**
   * Get the count of items still in progress or waiting
   * Only relevant when using real-time updates
   */
  getRemainingCount(): number {
    if (!this.hasRealTimeUpdates() || !this.dataSource) return 0;

    // Count items that are not in a final state
    return this.dataSource.filter((item: any) => !this.isStatusFinal(item.status)).length;
  }

  /**
   * Get the count of completed items
   * Only relevant when using real-time updates
   */
  getCompletedCount(): number {
    if (!this.hasRealTimeUpdates() || !this.dataSource) return 0;

    // Count items that are in a final state
    return this.dataSource.filter((item: any) => this.isStatusFinal(item.status)).length;
  }

  /**
   * Check if this component instance is using real-time updates
   */
  hasRealTimeUpdates(): boolean {
    return !!this.moveMessages$;
  }

  async exportToExcel() {
    await this.exportArrayToExcel(this.dataSource);
  }

  onCloseButtonClick() {
    // If processing is still ongoing and not all items are complete, prevent closing
    if (this.isProcessing && !this.areAllItemsComplete()) {
      // Do nothing - the button should be disabled anyway
      return;
    }

    // If processing is complete or all items are complete, allow closing
    // Only emit if the component is still in the DOM
    if (
      this._elementRef &&
      this._elementRef.nativeElement &&
      this._elementRef.nativeElement.isConnected
    ) {
      this.close.emit();
    }
  }

  onToListButtonClick() {
    // If processing is still ongoing and not all items are complete, prevent action
    if (this.isProcessing && !this.areAllItemsComplete()) {
      // Do nothing - the button should be disabled anyway
      return;
    }

    // If processing is complete or all items are complete, allow action
    this.toListButtonClick.emit();
  }

  buildColumnHeaderText(columnName: string): string {
    return columnName.charAt(0).toUpperCase() + columnName.slice(1);
  }

  getColorForDirection(direction: string): string {
    return MoveUserService.getColorForDirection(direction);
  }

  getIconForDirection(direction: string): string {
    return MoveUserService.getIconForDirection(direction);
  }

  /**
   * Get the CSS class for a status
   */
  getStatusClass(status: string): string {
    if (!status) return '';

    status = status.toLowerCase();
    // Generic status class mapping that works for all types of moves
    if (
      status.includes('success') ||
      status.includes('complete') ||
      status.includes('done') ||
      status.includes('ok')
    ) {
      return 'status-success';
    } else if (
      status.includes('error') ||
      status.includes('fail') ||
      status.includes('reject') ||
      status.includes('invalid')
    ) {
      return 'status-error';
    } else if (status.includes('progress') || status.includes('processing')) {
      return 'status-progress';
    } else if (status.includes('pending') || status.includes('wait') || status.includes('queue')) {
      return 'status-waiting';
    }
    return '';
  }

  async exportArrayToExcel(arr: any[]) {
    const XLSX = await import('xlsx');
    var wb = XLSX.utils.book_new();
    const textFields = ['minNumber'];
    const processedArr = arr.map(row => {
      const newRow: any = { ...row };
      textFields.forEach(field => {
        if (newRow[field] != null) {
          newRow[field] = newRow[field].toString();
        }
      });
      return newRow;
    });
    var ws = XLSX.utils.json_to_sheet(processedArr);
    // Translate the headers to natural language
    if (ws && ws['!ref']) {
      const range = XLSX.utils.decode_range(ws['!ref']);
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const address = XLSX.utils.encode_col(C) + '1';
        if (ws[address]) {
          const header = ws[address].v;
          ws[address].v = this.translateHeader(header);
        }
      }
    }

    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `${this.exportToExcelFileName}.xlsx`);
  }

  translateHeader(header: string): string {
    return this.translate.instant('MoveForms.General.' + this.buildColumnHeaderText(header));
  }

  /**
   * Gets the ID value from an item based on the determined ID property name
   */
  getItemId(item: any): number | undefined {
    return item ? item[this.idPropertyName] : undefined;
  }

  /**
   * Update the completion status based on the current data
   */
  private updateCompletionStatus() {
    // Check if all items are complete
    const allComplete = this.areAllItemsComplete();

    this.logger.log(
      `Checking completion status: allComplete=${allComplete}, isProcessing=${this.isProcessing}`
    );

    if (this.isProcessing && allComplete) {
      this.logger.log('All items are complete, updating processing state');
      this.isProcessing = false;

      // Update the subtitle to indicate completion
      this.subtitle = this.translate.instant('MoveForms.General.MoveStatusCompleteSubtitle');

      // Refresh the table if available
      if (this.table) {
        this.table.renderRows();
      }

      this._cdr.markForCheck();
      this._cdr.detectChanges();
    }
  }
}
